import { Cloudinary } from "@cloudinary/url-gen"
import { Resize } from "@cloudinary/url-gen/actions/resize"
import { Gravity } from "@cloudinary/url-gen/qualifiers"
import { useMemo } from "react"

import {
  LiveConcertExtract,
  Sliders,
  SliderType,
  TeaserDimension,
  TTeaser,
} from "../components/Sliders/Slider"
import {
  ArtistCollectionPageQuery,
  AudioDetailPageQuery,
  CuratedContentPageQuery,
  EditorialPlaylistPageQuery,
  EpochPageQuery,
  GenrePageQuery,
  GroupPageQuery,
  PartnerPageQuery,
  PublicationLevel,
  SliderLayout,
  VideoDetailPageQuery,
  WorkPageQuery,
} from "../generated/graphql"
import {
  mapSliderType,
  mapTeaserDimensions,
} from "../screens/Detail/DetailScreen"
import { appStore, GeoInfo } from "../store/AppStore"
import { ArrayElement } from "../types"
import {
  ccFactor,
  CloudinaryConf,
  CuratedContentSlugV2,
  s,
  secondsToMinutes,
} from "../utilities/common"
import { locale, maxSliderSize } from "../utilities/constants"
import { isContentAvailable } from "../utilities/content-availability-helpers"

import useIntl from "./use-intl"
import useTranslate from "./use-translate"

const cld = new Cloudinary(CloudinaryConf)

type CuratedContent = ArrayElement<
  CuratedContentPageQuery["curatedContentPage"]["curatedContent"]
>
type CuratedSlider = Extract<CuratedContent, { __typename: "ListSlider" }>
type VideoNodeWithCuratedContent = Extract<
  Exclude<VideoDetailPageQuery["node"], undefined>,
  { curatedContent: unknown }
>
type VideoNodeCuratedContent = ArrayElement<
  VideoNodeWithCuratedContent["curatedContent"]
>
type VideoNodeSlider = Extract<
  VideoNodeCuratedContent,
  { __typename: "ListSlider" }
>
type AudioCuratedContent = ArrayElement<
  Exclude<AudioDetailPageQuery["album"], undefined>["curatedContent"]
>
type AudioSlider = Extract<AudioCuratedContent, { __typename: "ListSlider" }>
type ArtistCuratedContent = ArrayElement<
  Exclude<ArtistCollectionPageQuery["artist"], undefined>["curatedContent"]
>
type ArtistSlider = Extract<ArtistCuratedContent, { __typename: "ListSlider" }>
type GroupCuratedContent = ArrayElement<
  Exclude<GroupPageQuery["group"], undefined>["curatedContent"]
>
type GroupSlider = Extract<GroupCuratedContent, { __typename: "ListSlider" }>
type PartnerCuratedContent = ArrayElement<
  Exclude<PartnerPageQuery["partner"], undefined>["curatedContent"]
>
type PartnerSlider = Extract<
  PartnerCuratedContent,
  { __typename: "ListSlider" }
>
type EpochCuratedContent = ArrayElement<
  Exclude<EpochPageQuery["epoch"], undefined>["curatedContent"]
>
type EpochSlider = Extract<EpochCuratedContent, { __typename: "ListSlider" }>
type GenreCuratedContent = ArrayElement<
  Exclude<GenrePageQuery["genre"], undefined>["curatedContent"]
>
type GenreSlider = Extract<GenreCuratedContent, { __typename: "ListSlider" }>
type WorkCuratedContent = ArrayElement<
  Exclude<WorkPageQuery["work"], undefined>["curatedContent"]
>
type WorkSlider = Extract<WorkCuratedContent, { __typename: "ListSlider" }>
type EditorialPlaylistCuratedContent = ArrayElement<
  Exclude<
    EditorialPlaylistPageQuery["editorialPlaylist"],
    undefined
  >["curatedContent"]
>
type EditorialPlaylistSlider = Extract<
  EditorialPlaylistCuratedContent,
  { __typename: "ListSlider" }
>

export type CuratedContentExtract =
  | CuratedContent
  | VideoNodeCuratedContent
  | AudioCuratedContent
  | ArtistCuratedContent
  | GroupCuratedContent
  | PartnerCuratedContent
  | EpochCuratedContent
  | GenreCuratedContent
  | WorkCuratedContent
  | EditorialPlaylistCuratedContent

export type SliderExtract =
  | CuratedSlider
  | VideoNodeSlider
  | AudioSlider
  | ArtistSlider
  | GroupSlider
  | PartnerSlider
  | EpochSlider
  | GenreSlider
  | WorkSlider
  | EditorialPlaylistSlider

// Filter non-queried types
type SliderItemExtract = ArrayElement<SliderExtract["items"]>
type BaseNodeExtract = Exclude<
  SliderItemExtract,
  {
    __typename: "Favorite" | "PlaybackProgress" | "BlogArticleLink" | "Playback"
  }
>
export type NodeExtract = Extract<BaseNodeExtract, { id: string }>

const hasLiveConcertStartTime = (
  node: NodeExtract,
): node is NodeExtract & { __typename: "LiveConcert"; startTime: string } =>
  node.__typename === "LiveConcert" &&
  "startTime" in node &&
  typeof node.startTime === "string"

const hasLiveConcertPayload = (node: NodeExtract): node is LiveConcertExtract =>
  node.__typename === "LiveConcert" &&
  "streamStartTime" in node &&
  "startTime" in node &&
  "endTime" in node &&
  "reruns" in node

const getLiveTitles = (slider: SliderExtract, geoInfo?: GeoInfo) => {
  const dates: Record<number, number> = {}
  const formattedDates: Record<number, string> = {}
  slider.items
    .filter((edge): edge is NodeExtract => "id" in edge)
    .filter((edge) => isContentAvailable(edge, geoInfo))
    .filter(hasLiveConcertStartTime)
    .map((node, i) => {
      const date = new Date(node.startTime)
      const formattedDate = date.getMonth()
      if (!Object.values(dates).includes(formattedDate)) {
        dates[i] = formattedDate
        const dateT = new Date()
        dateT.setMonth(formattedDate)
        formattedDates[i] = date.toLocaleString(locale, { month: "long" })
      }
    })
  return formattedDates
}

export const useSliders = (
  sliders?: CuratedContentExtract[],
  slug?: string,
) => {
  const withLiveData = slug === CuratedContentSlugV2.Live
  const { dateFormat } = useIntl()
  const t = useTranslate()
  const geoInfo = appStore((state) => state.geoInfo)

  return useMemo((): Sliders | undefined => {
    if (!sliders) return undefined
    return sliders
      .filter((slider) => slider.__typename === "ListSlider")
      .map((slider) => {
        if (!slider.items?.length) return undefined
        const sliderLayout = "layout" in slider ? slider.layout : undefined
        return {
          id: slider.id,
          title: slider.headline,
          layout: sliderLayout,
          totalResults: slider.totalResults,
          liveTitles:
            withLiveData && slider.items[0].__typename === "LiveConcert"
              ? getLiveTitles(slider, geoInfo)
              : undefined,
          type:
            slug === CuratedContentSlugV2.Search
              ? SliderType.Navigation
              : mapSliderType(slider.items[0].__typename),
          style: withLiveData
            ? TeaserDimension.LiveTeaser
            : mapTeaserDimensions(slider.items[0].__typename, sliderLayout),
          pageInfo: {
            hasNextPage:
              withLiveData ||
              (sliderLayout === SliderLayout.WeeklyTop10 &&
                slider.totalResults <= 10) ||
              slider.items?.[0]?.__typename === "CuratedContentLink"
                ? false
                : slider.totalResults > maxSliderSize,
          },
          teasers: slider.items
            // remove empty edges because we run multiple queries of different sliderPageSizes
            .filter((edge): edge is NodeExtract => "id" in edge)
            .filter((edge) => {
              // ensure that we only have CuratedContentLinks on Search page
              return slug === CuratedContentSlugV2.Search
                ? edge.__typename === "CuratedContentLink"
                : edge.__typename === "Video" ||
                    edge.__typename === "VodConcert" ||
                    edge.__typename === "LiveConcert" ||
                    edge.__typename === "Album" ||
                    edge.__typename === "EditorialPlaylist" ||
                    edge.__typename === "Artist" ||
                    edge.__typename === "Partner" ||
                    edge.__typename === "Group" ||
                    edge.__typename === "Epoch" ||
                    edge.__typename === "Work" ||
                    edge.__typename === "CuratedContentLink" ||
                    edge.__typename === "Genre"
            })
            // remove dupes
            .filter(
              (value, index, self) =>
                index ===
                self.findIndex((t) => {
                  return t.id === value.id
                }),
            )
            .filter((edge) => isContentAvailable(edge, geoInfo))
            .map((edge): TTeaser => {
              const pictureBase =
                edge.__typename === "Video" ||
                edge.__typename === "VodConcert" ||
                edge.__typename === "LiveConcert" ||
                edge.__typename === "Album" ||
                edge.__typename === "EditorialPlaylist" ||
                edge.__typename === "Artist" ||
                edge.__typename === "Partner" ||
                edge.__typename === "Group" ||
                edge.__typename === "Epoch" ||
                edge.__typename === "CuratedContentLink" ||
                edge.__typename === "Genre"
                  ? cld.image(
                      edge.pictures.filter((i) => {
                        return (
                          i.type ===
                          (edge.__typename === "Album" ||
                          edge.__typename === "EditorialPlaylist"
                            ? "COVER"
                            : edge.__typename === "Partner"
                              ? "LOGO"
                              : "TEASER")
                        )
                      })[0]?.url,
                    )
                  : undefined
              const pictureExtension =
                sliderLayout === SliderLayout.WeeklyTop10
                  ? pictureBase?.resize(
                      Resize.fill() // c_fill
                        .width(s(ccFactor(587), true))
                        .height(s(ccFactor(587), true))
                        .gravity(Gravity.autoGravity()),
                    )
                  : pictureBase
              const picture = pictureExtension
                ?.resize(Resize.scale().width(s(ccFactor(587), true)))
                .format("jpg")
                .setDeliveryType("fetch")
                .toURL()
              return {
                id: edge.id,
                uniqueId: edge.id,
                slugV2:
                  edge.__typename === "CuratedContentLink"
                    ? edge.slugV2
                    : undefined,
                type: edge.__typename,
                title:
                  edge.__typename === "VodConcert" ||
                  edge.__typename === "Video" ||
                  edge.__typename === "Album" ||
                  edge.__typename === "LiveConcert" ||
                  edge.__typename === "Epoch" ||
                  edge.__typename === "Genre" ||
                  edge.__typename === "EditorialPlaylist" ||
                  edge.__typename === "CuratedContentLink" ||
                  edge.__typename === "Work"
                    ? edge.title
                    : edge.__typename === "Artist" ||
                        edge.__typename === "Partner" ||
                        edge.__typename === "Group"
                      ? edge.name
                      : "",
                subtitle:
                  edge.__typename === "VodConcert" ||
                  edge.__typename === "Video" ||
                  edge.__typename === "LiveConcert" ||
                  edge.__typename === "Epoch"
                    ? edge.subtitle
                    : edge.__typename === "Album"
                      ? edge.artistAndGroupDisplayInfo
                      : edge.__typename === "Artist"
                        ? edge.roles.map((r) => r.displayName).join(", ")
                        : edge.__typename === "Group"
                          ? edge.typeDisplayName
                          : edge.__typename === "Work"
                            ? edge.composers.map((c) => c.name).join(", ")
                            : undefined,
                caption:
                  edge.__typename === "LiveConcert" &&
                  "streamStartTime" in edge &&
                  edge.streamStartTime
                    ? dateFormat(new Date(edge.streamStartTime), "shortTime")
                    : edge.__typename === "VodConcert"
                      ? t("duration__in_minutes", [
                          secondsToMinutes(edge.totalDuration),
                        ])
                      : edge.__typename === "Video"
                        ? t("duration__in_minutes", [
                            secondsToMinutes(edge.duration),
                          ])
                        : undefined,
                liveConcert:
                  withLiveData && hasLiveConcertPayload(edge)
                    ? edge
                    : undefined,
                label:
                  edge?.__typename === "VodConcert" ||
                  edge?.__typename === "Video"
                    ? edge.typeDisplayName
                    : edge?.__typename === "EditorialPlaylist"
                      ? edge?.typeDisplayName
                      : undefined,
                publicationLevel:
                  edge.__typename === "Video" ||
                  edge.__typename === "VodConcert" ||
                  edge.__typename === "LiveConcert"
                    ? edge.publicationLevel
                    : PublicationLevel.TicketRequired,
                liveConcertType:
                  edge.__typename === "LiveConcert"
                    ? edge.liveConcertType
                    : undefined,
                picture,
              }
            }),
        }
      })
      .filter((slider) => slider !== undefined)
  }, [sliders, dateFormat, t, geoInfo])
}
